<template>
  <div>
    <div class="grid grid-cols-12 gap-6">
      <div class="col-span-12 lg:col-span-4">
        <ChartCard title="การอาศัยอยู่กับพ่อและแม่">
          <div class="h-[400px]">
            <ChartWithLegend
              chart-id="pcLivingChart"
              :series="living.series"
              :labels="living.labels"
              unit="คน"
              :height="200"
              :show-data-label="true"
              :palette="livingPalette"
            />
          </div>
        </ChartCard>
      </div>
      <div class="col-span-12 lg:col-span-4">
        <ChartCard title="ผู้ทำหน้าที่ดูแลหลัก">
          <div class="h-[400px]">
            <ChartWithLegend
              chart-id="pcCaregiverChart"
              :series="caregiver.series"
              :labels="caregiver.labels"
              unit="คน"
              :height="200"
              :show-data-label="true"
              :palette="CAREGIVER_PALETTE"
            />
          </div>
        </ChartCard>
      </div>
      <div class="col-span-12 lg:col-span-4">
        <ChartCard title="เหตุผลที่ไม่ได้อยู่กับพ่อ/แม่" class="h-full">
          <div class="min-h-[400px]">
            <ProgressList :items="reasons" :color="P.navy" minHeight="400px" />
          </div>
        </ChartCard>
      </div>
    </div>

    <div class="mt-6">
      <ChartCard title="พฤติกรรมและทักษะสังคม">
        <div class="h-[400px]" v-if="!behavior.length">
          <div class="flex justify-center items-center h-full">
            <p class="text-sm text-gray-600">ไม่มีข้อมูล</p>
          </div>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-6" v-else>
          <div>
            <div v-for="section in behavior.slice(0, 3)" :key="section.key" class="mb-4 last:mb-0">
              <h3 class="text-md font-medium text-dark mb-2">{{ section.title }}</h3>
              <div class="min-h-[200px]">
                <ProgressList :items="section.items" :color="P.yellow" />
              </div>
            </div>
          </div>
          <div>
            <div v-for="section in behavior.slice(3)" :key="section.key" class="mb-4 last:mb-0">
              <h3 class="text-md font-medium text-dark mb-2">{{ section.title }}</h3>
              <div class="min-h-[200px]">
                <ProgressList :items="section.items" :color="P.yellow" />
              </div>
            </div>
          </div>
        </div>
      </ChartCard>
    </div>

    <div class="mt-6">
      <ChartCard title="การรับวัคซีน">
        <div class="h-[450px]">
          <DashboardBarEChart
            chart-id="pcVaccineChart"
            type="bar"
            :data="vaccineData"
            :options="vaccineOptions"
            fill-height
          />
        </div>
      </ChartCard>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import ChartCard from "../../container/ChartCard.vue";
import ChartWithLegend from "../../container/ChartWithLegend.vue";
import ProgressList from "../../container/ProgressList.vue";
import DashboardBarEChart from "../../container/DashboardBarEChart.vue";
import { globalNumDecFixed } from "@/utils/format-number.js";

const props = defineProps({
  data: { type: Object, default: () => ({}) },
});

const P = { navy: "#3A506A", blue: "#20A0D7", yellow: "#F4D35E", green: "#11D67A", red: "#F73C4B", gray: "#606468", purple: "#AF52DE", orange: "#fe9000" };

const LIVING_COLOR_BY_LABEL = {
  "อยู่กับทั้งพ่อและแม่": "#345E7E",
  "อยู่กับแม่คนเดียว": "#2CA6BD",
  "อยู่กับพ่อคนเดียว": "#2FAE7E",
  "อยู่กับปู่ ย่า ตา และ/หรือ ยาย": "#E0A64A",
  "อยู่กับญาติอื่น ๆ": "#8B6BC2",
  "อื่น ๆ": "#6B7280",
};
const CAREGIVER_PALETTE = ["#D97D3D", "#5468BE", "#E2687A", "#2FAE7E", "#2CA6BD", "#345E7E"];

const bar = (label, color, data, opts = {}) => ({
  label, backgroundColor: color, data, barPercentage: 0.6, categoryPercentage: 0.8, ...opts,
});

const barOptions = ({ legend = false, stacked = false, horizontal = false, aspectRatio = 2 } = {}) => {
  const cat = { stacked, grid: { display: false }, ticks: { font: { size: 12 }, autoSkip: false, maxRotation: horizontal ? 0 : 45, minRotation: horizontal ? 0 : 45 } };
  const val = { stacked, beginAtZero: true, grid: { color: "#E2E8F0" }, ticks: { font: { size: 12 }, precision: 0 } };
  return {
    indexAxis: horizontal ? "y" : "x",
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio,
    plugins: {
      legend: { display: legend, position: "bottom", labels: { usePointStyle: true, boxWidth: 8, font: { size: 12 } } },
      tooltip: {
        enabled: true,
        callbacks: {
          label(ctx) {
            const numVal = horizontal ? ctx.parsed.x : ctx.parsed.y;
            return `${ctx.dataset.label}: ${globalNumDecFixed(numVal)} คน`;
          },
        },
      },
    },
    scales: horizontal ? { x: val, y: cat } : { x: cat, y: val },
  };
};


const toNumber = (value) => Number(value || 0);

const living = computed(() => {
  const items = props.data?.lives_with_parents || [];
  if (items.length) {
    return {
      labels: items.map((item) => item.name || ""),
      series: items.map((item) => toNumber(item.total)),
    };
  }
  return {
    labels: [],
    series: [],
  }
});
const livingPalette = computed(() =>
  living.value.labels.map((label) => LIVING_COLOR_BY_LABEL[label] || "#606468")
);

const caregiver = computed(() => {
  const items = props.data?.caregiver || [];
  if (items.length) {
    return {
      labels: items.map((item) => item.name || ""),
      series: items.map((item) => toNumber(item.total)),
    };
  }
  return {
    labels: [],
    series: [],
  }
});

const reasons = computed(() => {
  const items = props.data?.reason_not_with_parents || [];
  if (items.length) {
    return items.map((item) => ({
      label: item.name || "",
      percent: item.percentage || 0,
      value: toNumber(item.total),
    }));
  }
  return []
});

const BEHAVIOR_LABELS = {
  adaptation: "การปรับตัวกับเพื่อน",
  violence: "พฤติกรรมก้าวร้าว",
  hyperactivity: "อาการสมาธิสั้น",
  severe_illness: "การเจ็บป่วยรุนแรง",
  pre_school: "การเตรียมความพร้อมก่อนวัยเรียน",
};

const BEHAVIOR_COLOR_BY_LABEL = {
  adaptation: { "ดี": P.green, "ปานกลาง": P.orange, "ไม่ดี": P.red },
  severe_illness: { "เคย": P.red, "ไม่เคย": P.green },
  violence: { "ไม่มี": P.green, "บางครั้ง": P.orange, "บ่อย": P.red },
  pre_school: { "ไม่ได้รับ": P.red },
  hyperactivity: { "ไม่มี": P.green, "บางส่วน": P.orange, "มีทั้งหมด": P.red },
};

const behavior = computed(() => {
  const raw = props.data?.behavior;
  if (!raw || Array.isArray(raw)) return [];
  return Object.entries(raw).map(([key, items]) => ({
    key,
    title: BEHAVIOR_LABELS[key] || key,
    items: (items || []).map((item) => ({
      label: item.name || "",
      percent: toNumber(item.percentage),
      value: toNumber(item.total),
      color: BEHAVIOR_COLOR_BY_LABEL[key]?.[item.name || ""],
    })),
  }));
});

const vaccineData = computed(() => {
  const items = props.data?.vaccine || [];
  const vaccineDisplay = [
    { key: "d1", display: "น้อยกว่าหรือเท่ากับ 6 เดือน" },
    { key: "d2", display: "6 เดือน - 1 ปี" },
    { key: "d3", display: "1 - 2.5ปี" },
    { key: "d4", display: "2.5 - 4 ปี" },
    { key: "d5", display: "4 - 7 ปี" },
    { key: "d6", display: "7 - 11 ปี" },
    { key: "d7", display: "11 ปีขึ้นไป" }
  ]
  const vaccineMap = {
    "น้อยกว่าหรือเท่ากับ 6 เดือน": "d1",
    "6เดือน - 1ปี": "d2",
    "6 เดือน - 1 ปี": "d2",
    "1 - 2.5ปี": "d3",
    "1 - 2.5 ปี": "d3",
    "2.5-4 ปี": "d4",
    "2.5 - 4 ปี": "d4",
    "4 - 7 ปี": "d5",
    "7 - 11 ปี": "d6",
    "11 ปีขึ้นไป": "d7"
  };

  const getTotalsByStatus = (statusName) => {
    return vaccineDisplay.map((dp) => {
      const found = items.find((item) => {
        const itemKey = vaccineMap[(item.vaccine_age_group || "")];
        const targetKey = dp.key;

        if (statusName === null) {
          return itemKey === targetKey && item.vaccine_status === null;
        }
        return itemKey === targetKey && item.vaccine_status === statusName;
      });

      return found ? Number(found.total || 0) : 0;
    });
  };
  if (items.length) {
    return {
      labels: vaccineDisplay.map((item) => item.display || ""),
      datasets: [
        bar("ได้รับวัคซีนครบ", P.green, getTotalsByStatus("ได้รับวัคซีนครบ"), { barPercentage: 0.7, categoryPercentage: 0.8 }),
        bar("ได้รับวัคซีนไม่ครบตามเกณฑ์", P.orange, getTotalsByStatus("ได้รับวัคซีนไม่ครบตามเกณฑ์"), { barPercentage: 0.7, categoryPercentage: 0.8 }),
        bar("ไม่เคยได้รับวัคซีน", P.red, getTotalsByStatus("ไม่เคยได้รับวัคซีน"), { barPercentage: 0.7, categoryPercentage: 0.8 }),
        bar("ไม่ทราบ/ไม่แน่ใจว่าได้รับวัคซีนครบตามเกณฑ์", P.yellow, getTotalsByStatus("ไม่ทราบ/ไม่แน่ใจว่าได้รับวัคซีนครบตามเกณฑ์"), { barPercentage: 0.7, categoryPercentage: 0.8 }),
        bar("อื่น ๆ", P.blue, getTotalsByStatus(null), { barPercentage: 0.7, categoryPercentage: 0.8 }),
      ],
    };
  }
  return {
    labels: [],
    datasets: [],
  }
});
const vaccineOptions = barOptions({ legend: true, stacked: true, aspectRatio: 1.8 });
</script>
